// Copyright (c) 2022 by Move Closer

import { Injectable } from '@movecloser/front-core'

import { IPrivacyManager, PreferenceRecord, PrivacyConfig } from './privacy.contracts'

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
@Injectable()
export class PrivacyManager implements IPrivacyManager {
  protected booted: boolean = false
  protected config: PrivacyConfig
  protected preferences: PreferenceRecord = {}
  protected storage: Storage
  protected storageKey: string

  /**
   * @param storage
   * @param config
   * @param storageKey
   */
  constructor (storage: Storage, config: PrivacyConfig, storageKey: string = 'privacy_preferences') {
    this.config = config
    this.storage = storage
    this.storageKey = storageKey

    this.boot()
  }

  public get isEnabled (): boolean {
    return this.config.enabled
  }

  public get options (): string[] {
    return this.config.options
  }

  public get requiredOptions (): string[] {
    return this.config.required
  }

  /**
   * @inheritDoc
   */
  public clearPreferences (): void {
    this.storage.setItem(this.storageKey, JSON.stringify({}))
  }

  /**
   * @inheritDoc
   */
  public getPreferences (): PreferenceRecord {
    if (!this.booted) {
      this.boot()
    }

    if (!this.hasAllPreferencesSet()) {
      this.mapPreferences(
        this.loadPreferences()
      )
    }

    return this.preferences
  }

  /**
   * @inheritDoc
   */
  public getValueOf (key: string): boolean {
    const value: boolean | undefined | null = this.preferences[key]
    if (typeof value !== 'boolean') {
      return false
    }

    return value
  }

  /**
   * @inheritDoc
   */
  public hasAllPreferencesSet (): boolean {
    return Object.keys(this.preferences).length === this.config.options.length
  }

  /**
   * @inheritDoc
   */
  public setPreferences (preferences: PreferenceRecord): void {
    this.storage.setItem(this.storageKey, JSON.stringify(preferences))
    this.mapPreferences(preferences)
  }

  /**
   * Loads data from storage.
   */
  protected boot (): void {
    this.mapPreferences(
      this.loadPreferences()
    )

    this.booted = true
  }

  /**
   * Load data from Storage.
   */
  protected loadPreferences (): PreferenceRecord {
    return JSON.parse(this.storage.getItem(this.storageKey) || '{}')
  }

  /**
   * Compose record with required agreements.
   *
   * @param preferences
   */
  protected mapPreferences (preferences: PreferenceRecord): void {
    const mapped: PreferenceRecord = {}

    for (const [key, value] of Object.entries(preferences)) {
      mapped[key] = typeof value === 'boolean' ? value : null
    }

    this.preferences = mapped
  }
}
